import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { HStack, Heading, IconButton, Spinner, Stack, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { useClassFiltersStore } from '@stores/classes';
import {
  addDays,
  eachDayOfInterval,
  endOfWeek,
  format,
  getISOWeek,
  getISOWeekYear,
  isSameDay,
  isToday,
  setISOWeek,
  startOfISOWeek,
  startOfWeek,
  subDays,
} from 'date-fns';
import { fr } from 'date-fns/locale';
import type { UseListClassesParams } from 'hooks/useApiQueries';
import { useListClasses, useListLocations, useListVisits } from 'hooks/useApiQueries';
import { useCoachStudioParams } from 'hooks/useCoachStudioParams';
import { useUserContext } from 'providers/useUserContext';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsMobile } from 'utils/functions';
import { trackViewPlanning } from 'utils/tracking';
import { resolveUserError } from 'utils/userErrorMessages';
import { ClassesLoading } from './Loading';
import MobileClasses from './MobileClasses';
import { ClassCard } from './components/Card';
import { ClassesFilters } from './components/Filters';
import { isCourseTypeFilterEnabled, matchesCourseType } from './courseTypeFilters';

// const brandName = mondayMindbody?.customization?.brandName;

function Classes() {
  const { isLoggedIn } = useUserContext();
  const { classType } = useClassFiltersStore();
  const location = useLocation();
  const { id, isStudioUrl, weekNumber } = useCoachStudioParams();

  useEffect(() => {
    // Réinitialiser le type de cours si l'URL ne contient pas de filtre
    if (!location.pathname.includes('/cours/')) {
      useClassFiltersStore.getState().setClassType([]);
    }
  }, []);

  // Réinitialiser les filtres coach et studio au montage uniquement si l'URL n'a pas /studio/:id
  // (sinon on écraserait la présélection du studio venue de l'URL, ex. #/studio/10/week/12)
  useEffect(() => {
    if (isStudioUrl) return;
    const store = useClassFiltersStore.getState();
    store.setCoachIds([]);
    store.setStudioIds([]);
  }, [isStudioUrl]);

  // Initialize date state with the current date or URL weekNumber
  const setDate = useClassFiltersStore((state) => state.setDate);
  const storedDate = useClassFiltersStore((state) => state.date);

  // Ne prendre le weekNumber de l'URL en compte qu'au premier render
  // pour ne pas écraser la navigation par flèches (setNextWeek/setPreviousWeek)
  const hasInitializedFromUrl = useRef(false);

  useEffect(() => {
    if (hasInitializedFromUrl.current) return;
    if (weekNumber !== null && weekNumber >= 1 && weekNumber <= 53) {
      const isoYear = getISOWeekYear(new Date());
      const targetDate = startOfISOWeek(setISOWeek(new Date(isoYear, 0, 4), weekNumber));
      setDate(targetDate);
      hasInitializedFromUrl.current = true;
    }
  }, [weekNumber, setDate]);

  const date = storedDate || new Date();

  const setNextWeek = useClassFiltersStore((state) => state.setNextWeek);
  const setPreviousWeek = useClassFiltersStore((state) => state.setPreviousWeek);

  // Custom function to calculate the correct week and year using ISO week standard
  const calculateWeekAndYear = (date: Date) => {
    // Use getISOWeekYear instead of getYear to correctly handle year transitions
    // For example, Dec 29-31 2025 are in ISO week 1 of 2026
    const week = getISOWeek(date);
    const year = getISOWeekYear(date);

    return { week, year };
  };

  const [currentWeek, setCurrentWeek] = useState(calculateWeekAndYear(date).week);
  const [currentYear, setCurrentYear] = useState(calculateWeekAndYear(date).year);

  useEffect(() => {
    const { week, year } = calculateWeekAndYear(date);
    setCurrentWeek(week);
    setCurrentYear(year);
  }, [date]);

  // Update params with the current week and year
  const [params, setParams] = useState<UseListClassesParams>(() => ({
    week: currentWeek,
    year: currentYear,
    studioId: null,
    coachId: null,
  }));

  useEffect(() => {
    setParams((prevParams: UseListClassesParams) => ({
      ...prevParams,
      week: currentWeek,
      year: currentYear,
    }));
  }, [currentWeek, currentYear]);

  const { studioIds, coachIds } = useClassFiltersStore();
  const { data: studios } = useListLocations();
  const currentStudioId = isStudioUrl ? id : studioIds[0];

  // Update studioId in params (coachIds is handled in useListClasses hook)
  useEffect(() => {
    setParams((prevParams: UseListClassesParams) => ({
      ...prevParams,
      // Si un seul studio est sélectionné, on utilise l'API Mindbody
      studioId: studioIds.length === 1 ? studioIds[0] : null,
    }));
  }, [studioIds]);

  // Trouver le studio actuel par studioId
  const currentStudio = studios?.find((studio) => {
    return parseInt(mondayMindbody?.siteId?.toString() || '0', 10) === studio.SiteID && studio.Id === currentStudioId;
  });

  const TitleComponent = () => {
    if (currentStudio && studioIds.length === 1) {
      return (
        <Heading textAlign="center" as="h1">
          {currentStudio.Name}
        </Heading>
      );
    } else {
      return (
        <>
          <Heading as="h1" textAlign="center">
            Planning
          </Heading>
          <Text as="h2" fontSize="lg" color="gray.600" textAlign="center">
            Tous studios
          </Text>
        </>
      );
    }
  };

  // Fonctions pour naviguer d'un jour à l'autre
  const handleNextDay = () => {
    const nextDay = addDays(date, 1);
    setDate(nextDay);
  };

  const handlePreviousDay = () => {
    const previousDay = subDays(date, 1);
    setDate(previousDay);
  };

  const { data: classesData, isLoading: isClassesLoading, isError } = useListClasses(params);

  // Tunnel de réservation : planning affiché (une fois par jeu de paramètres chargé).
  useEffect(() => {
    if (classesData) trackViewPlanning({ key: JSON.stringify(params), count: classesData.length });
  }, [classesData, params]);

  const filteredClassesData = classesData?.filter((classData) => {
    // Filtre par type de cours (selon la config du site + toggle back-office).
    // Le matching se fait sur le nom du cours, cf. courseTypeFilters.ts.
    if (isCourseTypeFilterEnabled() && Array.isArray(classType) && classType.length > 0) {
      if (!matchesCourseType(classData.ClassDescription.Name, classType, mondayMindbody?.siteId)) {
        return false;
      }
    }

    // Filtre par studio (si un ou plusieurs studios sont sélectionnés)
    if (studioIds.length > 0 && !studioIds.includes(classData.Location.Id)) {
      return false;
    }

    // Filtre par coach (si un ou plusieurs coaches sont sélectionnés)
    if (coachIds.length > 0 && !coachIds.includes(classData.Staff.Id)) {
      return false;
    }

    return classData.Active; // Aucun filtre appliqué, retourne tous les cours actifs
  });

  const { isLoading: isVisitsLoading } = useListVisits() || {};

  const startOfWeekDate = startOfWeek(date, { weekStartsOn: 1 });
  const endOfWeekDate = endOfWeek(date, { weekStartsOn: 1 });
  const days = eachDayOfInterval({
    start: startOfWeekDate,
    end: endOfWeekDate,
  });

  const isLoading = isClassesLoading || (isLoggedIn && isVisitsLoading);

  const isMobile = useIsMobile();
  const planningError = resolveUserError('loadClasses');

  // Logique pour afficher la vue mobile
  if (isMobile) {
    return (
      <>
        <Stack alignItems="center">
          <TitleComponent />
          <ClassesFilters />
          {isClassesLoading ? (
            <Spinner mt={2} size="xl" />
          ) : (
            <MobileClasses
              date={date}
              classesData={filteredClassesData || []}
              onNextDay={handleNextDay}
              onPreviousDay={handlePreviousDay}
            />
          )}
        </Stack>
      </>
    );
  }

  return (
    <Stack alignItems="center">
      <Heading as="div" className="mainAppTitle" display={mondayMindbody.coach ? 'none' : 'block'}>
        <TitleComponent />
      </Heading>
      <ClassesFilters />
      <Table marginTop="-1px" variant="unstyled" layout="fixed" className="planningTable">
        <Thead className="planningTableHead">
          <Tr className="calendarDays">
            {days.map((day, index) => (
              <Th key={index} className="calendarDay" backgroundColor={isToday(day) ? '#D9D9D9' : '#F5F5F5'}>
                <Stack spacing={1} flexGrow={1} alignItems="center" textAlign="center">
                  <Text
                    mb="1"
                    letterSpacing={0}
                    textTransform="capitalize"
                    fontSize="md"
                    color="black"
                    fontWeight="semibold"
                  >
                    {format(day, 'EEEE', { locale: fr })} {/* Utilise 'EEEE' pour le jour complet en français */}
                  </Text>
                  <Text letterSpacing={0} fontSize="xs" fontWeight="semibold">
                    {format(day, 'dd/MM', { locale: fr })} {/* Formate la date en '29/01' */}
                  </Text>
                </Stack>
              </Th>
            ))}
          </Tr>
        </Thead>

        <Tbody className="planningTableBody">
          <Tr>
            {filteredClassesData?.length === 0 ? (
              <Td colSpan={days.length}>
                <Stack spacing={0} h="full">
                  <Text align="center" className="noClasses">
                    Aucune séance trouvée
                  </Text>
                </Stack>
              </Td>
            ) : isError ? (
              <Td colSpan={days.length}>
                <Text fontSize="3xl" fontWeight="medium" textAlign="center">
                  {planningError.title}
                </Text>
                <Text fontSize="md" color="gray.600" textAlign="center" mt={2}>
                  {planningError.description}
                </Text>
              </Td>
            ) : isLoading ? (
              days.map((_, index) => (
                <Td key={index} p={2} verticalAlign="top">
                  <ClassesLoading />
                </Td>
              ))
            ) : (
              days.map((day, index) => {
                const classes = ((Array.isArray(filteredClassesData) && filteredClassesData) || [])?.filter((current) =>
                  isSameDay(day, new Date(current.StartDateTime)),
                );
                return (
                  <Td key={index} p={0} verticalAlign="top" borderLeft="1px solid #D9D9D9">
                    <Stack spacing={0} h="full">
                      {classes.map((mindbodyClass) => (
                        <ClassCard key={mindbodyClass.Id} mindbodyClass={mindbodyClass} />
                      ))}
                    </Stack>
                  </Td>
                );
              })
            )}
          </Tr>
        </Tbody>
      </Table>
      <HStack spacing={2} alignItems="flex-end" justifyContent="space-between" w="full" backgroundColor="#F5F5F5">
        <Text cursor="pointer" color="black" onClick={setPreviousWeek} className="previousButton">
          <IconButton
            backgroundColor="transparent"
            color="black"
            aria-label="Previous"
            className="previousIcon"
            icon={<ChevronLeftIcon backgroundColor="transparent" color="black" />}
          />
          semaine précédente
        </Text>
        <Text cursor="pointer" color="black" onClick={setNextWeek} className="nextButton">
          semaine suivante{' '}
          <IconButton
            backgroundColor="transparent"
            color="black"
            aria-label="Next"
            className="nextIcon"
            icon={<ChevronRightIcon backgroundColor="transparent" color="black" />}
          />
        </Text>
      </HStack>
      <HStack align="center" marginTop="0">
        <Text color="#D9D9D9" marginTop="0" className="bottomNote">
          * cours accessibles au packs étudiants
        </Text>
      </HStack>
    </Stack>
  );
}

export { Classes };
